import OrphanedPaymentsCard from "../Dashboard/OrphanedPaymentsCard";
import InvoiceStateBadge from "./InvoiceStateBadge";
import WaitingDots from 'components/WaitingDots';
import { Invoice } from 'models/Invoice';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Alert, Button, Card, CardBody, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { bindActionCreators } from 'redux';
import { RootState } from 'store/store';
import { addInvoicePayment, convertInvoice, deleteInvoice, finalizeInvoice } from "store/actions/Billing";
import { useNavigate } from 'react-router-dom';
import LongClickButton from 'components/LongClickButton';
import { ManualInvoicePayment } from 'services/Api';
import { useOfficeTranslation } from 'i18n/OfficeTranslation';
import {useInvoiceFormatting} from './useInvoiceFormatting';

interface Props {
    invoice: Invoice;
}

interface Actions {
    deleteInvoice: (i: Invoice) => Promise<any>;
    convertInvoice: (i: Invoice) => void;
    finalizeInvoice: (i: Invoice) => void;
    addInvoicePayment: (invoiceId: string, payment: ManualInvoicePayment) => Promise<any>;
}

const localDateValue = (): string => {
    const date = new Date();
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString().slice(0, 10);
};

const InvoiceStatusCard: React.FC<Props & Actions> = ({ invoice, deleteInvoice, convertInvoice, finalizeInvoice, addInvoicePayment }) => {
    const { t } = useOfficeTranslation();
    const {currency, date} = useInvoiceFormatting();

    const navigate = useNavigate();
    const [paymentOpen, setPaymentOpen] = useState(false);
    const [paymentDate, setPaymentDate] = useState(localDateValue());
    const [paymentIban, setPaymentIban] = useState("");
    const [paymentAmount, setPaymentAmount] = useState("");
    const [paymentError, setPaymentError] = useState("");
    const [paymentSaving, setPaymentSaving] = useState(false);

    const convert = () => {
        convertInvoice(invoice)
    }

    const finalize = () => {
        finalizeInvoice(invoice)
    }

    const openPayment = () => {
        setPaymentDate(localDateValue());
        setPaymentIban("");
        setPaymentAmount(Math.max(invoice.CalcPendingTotal(), 0).toFixed(2));
        setPaymentError("");
        setPaymentOpen(true);
    };

    const closePayment = () => {
        if (!paymentSaving) {
            setPaymentOpen(false);
            setPaymentError("");
        }
    };

    const savePayment = () => {
        const amount = Number(paymentAmount);
        if (!paymentDate) {
            setPaymentError(t("invoice.invoiceStatusCard.enterAPaymentDate"));
            return;
        }
        if (!Number.isFinite(amount) || amount <= 0) {
            setPaymentError(t("invoice.invoiceStatusCard.enterAnAmountGreaterThanZero"));
            return;
        }

        setPaymentSaving(true);
        setPaymentError("");
        addInvoicePayment(invoice.id, {
            date: paymentDate,
            iban: paymentIban.trim() || undefined,
            amount,
        })
            .then(() => setPaymentOpen(false))
            .catch(reason => setPaymentError(
                typeof reason === "string" ? reason : t("invoice.invoiceStatusCard.thePaymentCouldNotBeAdded")
            ))
            .finally(() => setPaymentSaving(false));
    };

    if (invoice === undefined) {
        return <WaitingDots />
    }

    const grandTotal = invoice.CalcGrandTotal();
    const receivedTotal = invoice.payments.reduce((total, payment) => total + payment.amount, 0);
    const showReceivedTotal = Math.round(receivedTotal * 100) !== Math.round(grandTotal * 100);

    if (invoice.quote) {
        return <Card>
            <CardBody>
                <h5 className="my-0 text-warning text-truncate"><i className="mdi mdi-block-helper me-3"></i>{t("common.quote")}</h5>
                <br />
                <Button color="primary" style={{ width: "100%" }}
                    onClick={() => {
                        convert()
                    }}>
                    {t("invoice.invoiceStatusCard.convertToInvoice")}</Button>
                <LongClickButton color="danger" style={{ marginTop: '5px', width: "100%" }}
                    onClick={() => {
                        deleteInvoice(invoice).then(() => {
                            navigate("/accounts/" + invoice.customer);
                        })
                    }}>
                    {t("invoice.invoiceStatusCard.deleteQuote")}</LongClickButton>
            </CardBody>
        </Card>
    }

    if (invoice.draft) {
        return <Card>
            <CardBody>
                <h5 className="my-0 text-warning text-truncate"><i className="mdi mdi-block-helper me-3"></i>{t("common.draft")}</h5>
                <br />
                {Number.isFinite(grandTotal) && grandTotal !== 0 &&
                    <Button color="primary" style={{ width: "100%" }}
                        onClick={() => {
                            finalize()
                        }}>{t("invoice.invoiceStatusCard.finalizeInvoice")}</Button>
                }
                <Button color="dark" style={{ marginTop: '5px', width: "100%" }}
                    onClick={() => {
                        convert()
                    }}>
                    {t("invoice.invoiceStatusCard.convertToQuote")}</Button>
                <LongClickButton color="danger" style={{ marginTop: '5px', width: "100%" }}
                    onClick={() => {
                        deleteInvoice(invoice).then(() => {
                            navigate("/accounts/" + invoice.customer);
                        })
                    }}>
                    {t("invoice.invoiceStatusCard.deleteDraft")}</LongClickButton>
            </CardBody>
        </Card>
    }

    return (
        <>
            <Card>
                <CardBody>
                <div className="invoice-payment-heading"><h5 className="invoice-section-title">{t("invoice.detail.payment")}</h5><InvoiceStateBadge invoice={invoice} /></div>
                {showReceivedTotal && <div className="invoice-payment-received"><span>{t("invoice.detail.received")}</span><strong>{currency(receivedTotal)}</strong></div>}
                {invoice.payments.length > 0 ? <div className="invoice-payment-list">
                    {[...invoice.payments].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(payment =>
                        <div className="invoice-payment-row" key={payment.id}>
                            <i className="mdi mdi-bank-outline invoice-payment-icon" aria-hidden="true" />
                            <div className="invoice-payment-identity">
                                <strong>{payment.type.includes("ideal") ? t("invoice.paymentMethods.ideal")
                                    : payment.type === "manual" ? t("invoice.paymentMethods.manual")
                                    : payment.type === "bank" ? t("invoice.paymentMethods.bank") : payment.type}</strong>
                                {payment.metadata?.counter_iban && <small className="text-muted">{payment.metadata.counter_iban}</small>}
                            </div>
                            <div className="invoice-payment-value">
                                <strong>{currency(payment.amount)}</strong>
                                <small className="text-muted">{payment.date && date(new Date(payment.date))}</small>
                            </div>
                        </div>)}
                </div> : <p className="text-muted mb-0">{t("invoice.detail.noPayment")}</p>}
                {!invoice.IsPaid() &&
                    <Button outline color="primary" className="mt-3 invoice-add-payment" onClick={openPayment}>
                        <i className="mdi mdi-plus me-1" />
                        {t("invoice.invoiceStatusCard.addManualPayment")}
                    </Button>
                }
                {!invoice.IsPaid() && <OrphanedPaymentsCard invoice={invoice} collapsible embedded />}
                </CardBody>
            </Card>

            <Modal centered isOpen={paymentOpen} toggle={closePayment}>
                <ModalHeader toggle={closePayment}>{t("invoice.invoiceStatusCard.addManualPayment")}</ModalHeader>
                <ModalBody>
                    {paymentError && <Alert color="danger">{paymentError}</Alert>}
                    <FormGroup>
                        <Label for="invoice-payment-date">{t("common.date")}</Label>
                        <Input
                            id="invoice-payment-date"
                            type="date"
                            value={paymentDate}
                            onChange={event => setPaymentDate(event.target.value)}
                            disabled={paymentSaving}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label for="invoice-payment-iban">{t("invoice.invoiceStatusCard.ibanOptional")}</Label>
                        <Input
                            id="invoice-payment-iban"
                            type="text"
                            value={paymentIban}
                            onChange={event => setPaymentIban(event.target.value)}
                            placeholder={t("invoice.invoiceStatusCard.ibanExample")}
                            autoComplete="off"
                            disabled={paymentSaving}
                        />
                    </FormGroup>
                    <FormGroup className="mb-0">
                        <Label for="invoice-payment-amount">{t("common.amount")}</Label>
                        <Input
                            id="invoice-payment-amount"
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={paymentAmount}
                            onChange={event => setPaymentAmount(event.target.value)}
                            disabled={paymentSaving}
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" type="button" onClick={closePayment} disabled={paymentSaving}>{t("common.cancel")}</Button>
                    <Button color="primary" type="button" onClick={savePayment} disabled={paymentSaving}>
                        {paymentSaving && <Spinner size="sm" className="me-2" />}
                        {t("invoice.invoiceStatusCard.savePayment")}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
}

const mapStateToProps = (state: RootState) => {
    return {
        invoices: state.Invoices,
    };
};


const mapDispatchToProps = (dispatch: any) => {
    return bindActionCreators({ deleteInvoice, convertInvoice, finalizeInvoice, addInvoicePayment }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceStatusCard);
